import type { ChatSettings } from "@nexus/types"
import type { PayloadAction } from "@reduxjs/toolkit"

import { STORAGE_KEYS } from "@/common/services/localStorage.keys"
import { storage } from "@/common/services/localStorage.service"

import type { PlaygroundChatContext, PlaygroundChatState } from "../playgroundTypes"
import { createChatInstance } from "../state"

/**
 * Open a chat (create if doesn't exist)
 */
export const openChat = (
  state: PlaygroundChatState,
  action: PayloadAction<{ chatId: string; context?: PlaygroundChatContext; isNew?: boolean }>
) => {
  const { chatId, context, isNew } = action.payload

  if (!state.chats[chatId]) {
    state.chats[chatId] = createChatInstance(chatId, context, isNew ?? true)
  }

  if (context) {
    state.chats[chatId].context = context
  }

  state.chats[chatId].lastAccessedAt = Date.now()
  state.activeChatId = chatId
}

/**
 * Close a chat (remove from state)
 */
export const closeChat = (
  state: PlaygroundChatState,
  action: PayloadAction<{ chatId: string }>
) => {
  const { chatId } = action.payload
  delete state.chats[chatId]

  if (state.activeChatId === chatId) {
    state.activeChatId = null
  }
}

/**
 * Set active chat
 */
export const setActiveChat = (
  state: PlaygroundChatState,
  action: PayloadAction<{ chatId: string | null }>
) => {
  state.activeChatId = action.payload.chatId
}

/**
 * Update chat settings
 */
export const updateChatSettings = (
  state: PlaygroundChatState,
  action: PayloadAction<{ chatId: string; settings: ChatSettings }>
) => {
  const { chatId, settings } = action.payload
  const chat = state.chats[chatId]

  if (chat) {
    chat.settings = settings
    storage.set(STORAGE_KEYS.PLAYGROUND_SHOW_TOOL_CALLS, settings.showToolCalls)
    storage.set(STORAGE_KEYS.PLAYGROUND_SHOW_THINKING, settings.showThinking)
  }
}

/**
 * Set the hasCodeInterpreterSession flag on a chat.
 * Used to clear the session flag after a sandbox reset so the next message
 * creates a fresh sandbox.
 */
export const setHasCodeInterpreterSession = (
  state: PlaygroundChatState,
  action: PayloadAction<{ chatId: string; hasSession: boolean }>
) => {
  const { chatId, hasSession } = action.payload
  const chat = state.chats[chatId]

  if (chat) {
    chat.hasCodeInterpreterSession = hasSession
  }
}

/**
 * Set the sandboxJustReset flag on a chat.
 * Bypasses the hasCodeInterpreterSession blocking check so the input is immediately
 * usable after an intentional reset. Cleared when sendMessage.pending fires.
 */
export const setSandboxJustReset = (
  state: PlaygroundChatState,
  action: PayloadAction<{ chatId: string; value: boolean }>
) => {
  const { chatId, value } = action.payload
  const chat = state.chats[chatId]

  if (chat) {
    chat.sandboxJustReset = value
  }
}

/**
 * Stop sending message - immediate UI feedback when stop is requested
 * Clears streaming state and sets isSendingMessage to false
 */
export const stopSending = (
  state: PlaygroundChatState,
  action: PayloadAction<{ chatId: string }>
) => {
  const { chatId } = action.payload
  const chat = state.chats[chatId]

  if (chat) {
    chat.isSendingMessage = false
    chat.shouldShowDynamicWhitespace = false
    chat.streamingMessages = {}
  }
}
